/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  CanonicalToolAttributes,
  CanonicalToolAttributes$inboundSchema,
} from "./canonicaltoolattributes.js";
import {
  ResponseFilter,
  ResponseFilter$inboundSchema,
} from "./responsefilter.js";
import {
  ToolAnnotations,
  ToolAnnotations$inboundSchema,
} from "./toolannotations.js";
import { ToolVariation, ToolVariation$inboundSchema } from "./toolvariation.js";

/**
 * An HTTP tool
 */
export type HTTPToolDefinition = {
  /**
   * Tool annotations providing behavioral hints about the tool
   */
  annotations?: ToolAnnotations | undefined;
  /**
   * The ID of the asset
   */
  assetId: string;
  /**
   * The original details of a tool
   */
  canonical?: CanonicalToolAttributes | undefined;
  /**
   * The canonical name of the tool. Will be the same as the name if there is no variation.
   */
  canonicalName: string;
  /**
   * Confirmation mode for the tool
   */
  confirm?: string | undefined;
  /**
   * Prompt for the confirmation
   */
  confirmPrompt?: string | undefined;
  /**
   * The creation date of the tool.
   */
  createdAt: Date;
  /**
   * The default server URL for the tool
   */
  defaultServerUrl?: string | undefined;
  /**
   * The ID of the deployment
   */
  deploymentId: string;
  /**
   * Description of the tool
   */
  description: string;
  /**
   * HTTP method for the request
   */
  httpMethod: string;
  /**
   * The ID of the tool
   */
  id: string;
  /**
   * The name of the tool
   */
  name: string;
  /**
   * The ID of the OpenAPI v3 document
   */
  openapiv3DocumentId?: string | undefined;
  /**
   * OpenAPI v3 operation
   */
  openapiv3Operation?: string | undefined;
  /**
   * The name of the source package
   */
  packageName?: string | undefined;
  /**
   * Path for the request
   */
  path: string;
  /**
   * The ID of the project
   */
  projectId: string;
  /**
   * Response filter metadata for the tool
   */
  responseFilter?: ResponseFilter | undefined;
  /**
   * JSON schema for the request
   */
  schema: string;
  /**
   * Version of the schema
   */
  schemaVersion?: string | undefined;
  /**
   * Security requirements for the underlying HTTP endpoint
   */
  security?: string | undefined;
  /**
   * Summarizer for the tool
   */
  summarizer?: string | undefined;
  /**
   * Summary of the tool
   */
  summary: string;
  /**
   * The tags list for this http tool
   */
  tags: Array<string>;
  /**
   * The URN of this tool
   */
  toolUrn: string;
  /**
   * The last update date of the tool.
   */
  updatedAt: Date;
  variation?: ToolVariation | undefined;
};

/** @internal */
export const HTTPToolDefinition$inboundSchema: z.ZodMiniType<
  HTTPToolDefinition,
  unknown
> = z.pipe(
  z.object({
    annotations: z.optional(ToolAnnotations$inboundSchema),
    asset_id: z.string(),
    canonical: z.optional(CanonicalToolAttributes$inboundSchema),
    canonical_name: z.string(),
    confirm: z.optional(z.string()),
    confirm_prompt: z.optional(z.string()),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    default_server_url: z.optional(z.string()),
    deployment_id: z.string(),
    description: z.string(),
    http_method: z.string(),
    id: z.string(),
    name: z.string(),
    openapiv3_document_id: z.optional(z.string()),
    openapiv3_operation: z.optional(z.string()),
    package_name: z.optional(z.string()),
    path: z.string(),
    project_id: z.string(),
    response_filter: z.optional(ResponseFilter$inboundSchema),
    schema: z.string(),
    schema_version: z.optional(z.string()),
    security: z.optional(z.string()),
    summarizer: z.optional(z.string()),
    summary: z.string(),
    tags: z.array(z.string()),
    tool_urn: z.string(),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    variation: z.optional(ToolVariation$inboundSchema),
  }),
  z.transform((v) => {
    return remap$(v, {
      "asset_id": "assetId",
      "canonical_name": "canonicalName",
      "confirm_prompt": "confirmPrompt",
      "created_at": "createdAt",
      "default_server_url": "defaultServerUrl",
      "deployment_id": "deploymentId",
      "http_method": "httpMethod",
      "openapiv3_document_id": "openapiv3DocumentId",
      "openapiv3_operation": "openapiv3Operation",
      "package_name": "packageName",
      "project_id": "projectId",
      "response_filter": "responseFilter",
      "schema_version": "schemaVersion",
      "tool_urn": "toolUrn",
      "updated_at": "updatedAt",
    });
  }),
);

export function httpToolDefinitionFromJSON(
  jsonString: string,
): SafeParseResult<HTTPToolDefinition, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => HTTPToolDefinition$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'HTTPToolDefinition' from JSON`,
  );
}
